import { Controller } from "@hotwired/stimulus"

// Autocomplete + Start Scan behavior for the GridSignal Opportunity
// Finder search bar.
//
// Flow:
//   1. User types a city or county name
//   2. Dropdown shows city/county matches from /api/geographies/search
//   3. Clicking a suggestion populates the input text ("Topeka, KS")
//      AND stashes the city/state/type in hidden fields on the scan
//      form so Start Scan can POST a real scan with no re-typing.
//   4. Hitting Start Scan:
//        - If hidden city/state are populated (user picked a
//          suggestion) → POST to /gridsignal/scans to actually start
//          a scan on that market. The MarketScan creation path
//          handles un-hiding, dedup, and redirect-to-progress.
//        - Otherwise → GET /gridsignal/scans/new with the typed
//          text as q, running the catalog search / filter path.
//
//   Also: typing into the input after a pick clears the stashed
//   city/state so the next submit doesn't fire a scan against a
//   stale selection.
export default class extends Controller {
  static targets = [
    "input", "results",
    "searchForm", "scanForm",
    "scanCity", "scanState", "scanType"
  ]

  connect() {
    this._timer = null
    this._outsideClick = this.outsideClick.bind(this)
    document.addEventListener("click", this._outsideClick)
    // When the user starts typing again after a pick, clear the
    // stashed selection so Start Scan runs a search instead of
    // scanning the old pick.
    if (this.hasInputTarget) {
      this._clearPickOnEdit = this.clearPick.bind(this)
      this.inputTarget.addEventListener("input", this._clearPickOnEdit)
    }
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick)
    if (this.hasInputTarget && this._clearPickOnEdit) {
      this.inputTarget.removeEventListener("input", this._clearPickOnEdit)
    }
    if (this._timer) clearTimeout(this._timer)
  }

  onInput() {
    clearTimeout(this._timer)
    const q = this.inputTarget.value.trim()
    if (q.length < 2) {
      this.hideResults()
      return
    }
    this._timer = setTimeout(() => this.search(q), 220)
  }

  async search(q) {
    try {
      const resp = await fetch(`/api/geographies/search?q=${encodeURIComponent(q)}&types=city,county`, {
        headers: { "Accept": "application/json" }
      })
      if (!resp.ok) { this.hideResults(); return }
      const rows = (await resp.json()).filter(r => r.geo_type === "city" || r.geo_type === "county")
      this.renderResults(rows)
    } catch (_e) {
      this.hideResults()
    }
  }

  renderResults(rows) {
    if (!this.hasResultsTarget) return
    if (!rows.length) {
      this.resultsTarget.innerHTML = `<div class="gs-market-search-empty">No city or county matches. Try "Topeka KS" or "Loudoun County VA".</div>`
      this.resultsTarget.style.display = "block"
      return
    }
    const cities   = rows.filter(r => r.geo_type === "city").slice(0, 8)
    const counties = rows.filter(r => r.geo_type === "county").slice(0, 8)

    let html = ""
    if (cities.length) {
      html += '<div class="gs-market-search-group">City</div>'
      html += cities.map(r => this.rowHtml(r, "city")).join("")
    }
    if (counties.length) {
      html += '<div class="gs-market-search-group">County</div>'
      html += counties.map(r => this.rowHtml(r, "county")).join("")
    }
    this.resultsTarget.innerHTML = html
    this.resultsTarget.style.display = "block"
  }

  rowHtml(row, type) {
    const label = row.display_name ||
      (type === "county"
        ? `${row.city} County, ${row.state}`
        : `${row.city}, ${row.state}`)
    const badge = type === "county" ? "County" : "City"
    const badgeClass = type === "county" ? "gs-market-search-badge--county" : "gs-market-search-badge--city"
    return `
      <div class="gs-market-search-item"
           data-action="click->gs-market-search#pick"
           data-value="${this.escape(label)}"
           data-city="${this.escape(row.city || "")}"
           data-state="${this.escape(row.state || "")}"
           data-type="${type}">
        <span class="gs-market-search-badge ${badgeClass}">${badge}</span>
        <span class="gs-market-search-label">${this.escape(label)}</span>
      </div>
    `
  }

  pick(event) {
    const btn = event.currentTarget
    const value = btn.dataset.value
    const city  = btn.dataset.city
    const state = btn.dataset.state
    const type  = btn.dataset.type || "city"
    if (value) this.inputTarget.value = value
    if (city && state) {
      if (this.hasScanCityTarget)  this.scanCityTarget.value  = city
      if (this.hasScanStateTarget) this.scanStateTarget.value = state
      if (this.hasScanTypeTarget)  this.scanTypeTarget.value  = type
    }
    this.hideResults()
    this.inputTarget.focus()
  }

  // Click handler on the Start Scan button. If the user picked a
  // specific city/county from the autocomplete, submit the hidden
  // scan form (POST → real scan). Otherwise let the enclosing
  // search form submit normally (GET → catalog search).
  startScan(event) {
    const city  = this.hasScanCityTarget  ? this.scanCityTarget.value.trim()  : ""
    const state = this.hasScanStateTarget ? this.scanStateTarget.value.trim() : ""
    if (city && state && this.hasScanFormTarget) {
      event.preventDefault()
      this.scanFormTarget.requestSubmit ? this.scanFormTarget.requestSubmit() : this.scanFormTarget.submit()
    }
    // else: fall through, enclosing form submits as GET to the
    // Opportunity Finder search as before.
  }

  // Typing in the input invalidates any previously-picked selection.
  // Prevents "picked Topeka, KS → edited the text to Reno → hit
  // Start Scan → accidentally scanned Topeka."
  clearPick() {
    if (this.hasScanCityTarget)  this.scanCityTarget.value  = ""
    if (this.hasScanStateTarget) this.scanStateTarget.value = ""
    if (this.hasScanTypeTarget)  this.scanTypeTarget.value  = ""
  }

  outsideClick(e) {
    if (!this.element.contains(e.target)) this.hideResults()
  }

  hideResults() {
    if (this.hasResultsTarget) this.resultsTarget.style.display = "none"
  }

  escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
